import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { m, AnimatePresence } from 'framer-motion';
import { Menu, X, Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Logo from './Logo';

const LANGUAGES = {
  fr: { flag: '🇫🇷', name: 'Français', code: 'FR' },
  en: { flag: '🇬🇧', name: 'English',  code: 'EN' },
  de: { flag: '🇩🇪', name: 'Deutsch',  code: 'DE' },
  es: { flag: '🇪🇸', name: 'Español',  code: 'ES' },
  ru: { flag: '🇷🇺', name: 'Русский',  code: 'RU' },
};

const Navigation = () => {
  const [isScrolled, setIsScrolled]         = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langOpen, setLangOpen]             = useState(false);
  const location                            = useLocation();
  const { t, language, setLanguage }        = useLanguage();
  const langRef                             = useRef(null);

  const isHome        = location.pathname === '/';
  const isTransparent = isHome && !isScrolled;

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY >= 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setLangOpen(false);
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!langOpen) return;
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langOpen]);

  const navLinks = [
    { path: '/services',     label: t?.nav?.services || 'Expertises'     },
    { path: '/realisations',     label: t?.nav?.projects || 'Réalisations'   },
    { path: '/projets-en-vente', label: t?.nav?.forSale  || 'En vente'       },
    { path: '/a-propos',         label: t?.nav?.about    || 'Notre histoire' },
    { path: '/actualites',   label: t?.nav?.news     || 'Actualités'     },
    { path: '/contact',      label: t?.nav?.contact  || 'Contact'        },
  ];

  const ctaLabel = t?.nav?.cta || 'Devis gratuit';

  const navBg = isTransparent
    ? 'bg-transparent'
    : 'bg-white border-b border-stone shadow-[0_1px_12px_rgba(0,0,0,0.06)]';

  const textColor   = isTransparent ? 'text-white/90' : 'text-ink/80';
  const textHover   = isTransparent ? 'hover:text-white' : 'hover:text-forest';
  const iconColor   = isTransparent ? 'text-white' : 'text-ink';
  const logoVariant = isTransparent ? 'dark' : 'light';

  const currentLang = LANGUAGES[language] || LANGUAGES.fr;

  const handleLangSelect = (code) => {
    setLanguage(code);
    setLangOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${navBg}`}
        style={{ height: '72px' }}
      >
        <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-10 xl:px-20 h-full">
          <div className="flex items-center justify-between h-full">

            {/* Left — Logo */}
            <Link to="/" className="relative z-10 flex-shrink-0">
              <Logo variant={logoVariant} size="md" />
            </Link>

            {/* Center — Desktop nav links */}
            <div className="hidden lg:flex items-center gap-6 xl:gap-10">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`
                      relative font-sans text-[13px] xl:text-sm font-medium tracking-wide whitespace-nowrap
                      transition-colors duration-200
                      ${isActive
                        ? (isTransparent ? 'text-white' : 'text-forest')
                        : `${textColor} ${textHover}`
                      }
                    `}
                  >
                    {link.label}
                    <span
                      className="absolute -bottom-0.5 left-0 h-px transition-all duration-300"
                      style={{
                        width:      isActive ? '100%' : '0',
                        background: '#E87722',
                      }}
                    />
                  </Link>
                );
              })}
            </div>

            {/* Right — Language switcher + CTA + hamburger */}
            <div className="flex items-center gap-3 lg:gap-4">

              {/* Language switcher — visible on all breakpoints (mobile shows it directly on the hero) */}
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setLangOpen((v) => !v)}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 select-none ${textColor} ${textHover}`}
                  aria-label="Switch language"
                >
                  <span className="text-base leading-none">{currentLang.flag}</span>
                  <span className="tracking-wide">{currentLang.code}</span>
                  <svg
                    className={`w-3 h-3 transition-transform duration-200 ${langOpen ? 'rotate-180' : ''}`}
                    viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
                  >
                    <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>

                <AnimatePresence>
                  {langOpen && (
                    <m.div
                      initial={{ opacity: 0, y: 6, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 6, scale: 0.97 }}
                      transition={{ duration: 0.15, ease: 'easeOut' }}
                      className="absolute right-0 mt-2 w-44 bg-white shadow-lg rounded border border-stone overflow-hidden"
                      style={{ zIndex: 60 }}
                    >
                      {Object.entries(LANGUAGES).map(([code, lang]) => {
                        const isSelected = code === language;
                        return (
                          <button
                            key={code}
                            onClick={() => handleLangSelect(code)}
                            className={`
                              w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left
                              transition-colors duration-150
                              ${isSelected
                                ? 'text-forest font-medium bg-stone/30'
                                : 'text-ink/80 hover:bg-stone/20'
                              }
                            `}
                          >
                            <span className="text-base leading-none">{lang.flag}</span>
                            <span className="flex-1">{lang.name}</span>
                            {isSelected && <Check className="w-3.5 h-3.5 text-forest flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </m.div>
                  )}
                </AnimatePresence>
              </div>

              {/* CTA button — desktop */}
              <Link
                to="/contact"
                className="hidden lg:inline-flex items-center whitespace-nowrap bg-forest text-white text-[13px] xl:text-sm px-4 xl:px-5 py-2.5 font-medium hover:bg-forest-600 transition-colors duration-200"
              >
                {ctaLabel}
              </Link>

              {/* Hamburger — mobile only */}
              <button
                onClick={() => setMobileMenuOpen((v) => !v)}
                className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors ${iconColor}`}
                aria-label="Menu"
              >
                <AnimatePresence mode="wait" initial={false}>
                  {mobileMenuOpen ? (
                    <m.span
                      key="close"
                      initial={{ rotate: -45, opacity: 0 }}
                      animate={{ rotate: 0,   opacity: 1 }}
                      exit={{   rotate:  45,  opacity: 0 }}
                      transition={{ duration: 0.18 }}
                    >
                      <X className="w-5 h-5" />
                    </m.span>
                  ) : (
                    <m.span
                      key="open"
                      initial={{ rotate:  45, opacity: 0 }}
                      animate={{ rotate:   0, opacity: 1 }}
                      exit={{   rotate: -45,  opacity: 0 }}
                      transition={{ duration: 0.18 }}
                    >
                      <Menu className="w-5 h-5" />
                    </m.span>
                  )}
                </AnimatePresence>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile full-screen menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <m.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-40 lg:hidden bg-white flex flex-col"
          >
            <div
              className="flex items-center justify-between px-6 border-b border-stone flex-shrink-0"
              style={{ height: '72px' }}
            >
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <Logo variant="light" size="md" />
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-10 h-10 flex items-center justify-center text-ink"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 flex flex-col px-8 py-10 overflow-y-auto">
              <nav className="flex-1">
                {navLinks.map((link, i) => (
                  <m.div
                    key={link.path}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.04 + i * 0.07, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Link
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`
                        block font-heading text-2xl leading-tight py-5 border-b border-stone
                        transition-colors duration-200
                        ${location.pathname === link.path ? 'text-forest' : 'text-ink/60 hover:text-forest'}
                      `}
                    >
                      {link.label}
                    </Link>
                  </m.div>
                ))}
              </nav>

              {/* CTA */}
              <m.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.34, duration: 0.4 }}
                className="mt-10"
              >
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center justify-center bg-forest text-white w-full h-14 text-sm font-medium hover:bg-forest-600 transition-colors duration-200"
                >
                  {ctaLabel}
                </Link>
              </m.div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
